import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { reportesApi } from '../../utils/api';
import { Landmark, Users, Receipt, FileSpreadsheet, FileX, X, Eye, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import DocumentDetailModal from '../../components/DocumentDetailModal';

const anioActual = new Date().getFullYear();
const ANIOS = ['todos', ...Array.from({ length: 5 }, (_, i) => anioActual - i)];

export default function RetencionesRenta() {
  const [anio, setAnio] = useState(anioActual);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [clienteSel, setClienteSel] = useState(null); // cliente cuyo detalle se muestra en el modal
  const [verFacturaId, setVerFacturaId] = useState(null); // FE en preview

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await reportesApi.getRetencionesRenta({ anio });
      setData(res.data.data);
    } catch (error) {
      toast.error('Error al cargar el reporte de retenciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [anio]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await reportesApi.exportRetencionesRenta({ anio });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Retenciones_Renta_${anio === 'todos' ? 'Todos' : anio}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  const crc = (v) => `₡${(parseFloat(v) || 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const money = (v, mon) => mon === 'USD'
    ? `$${(parseFloat(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : crc(v);
  const fecha = (d) => d ? new Date(d).toLocaleDateString('es-CR') : '-';

  // Detalle del cliente seleccionado
  const detalleCliente = useMemo(() => {
    if (!clienteSel || !data?.detalle) return [];
    return data.detalle.filter(d => String(d.clienteId) === String(clienteSel.clienteId));
  }, [clienteSel, data]);

  return (
    <div className="space-y-4">
      {/* Encabezado / descripción */}
      <div className="bg-white border border-violet-200 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-50 text-violet-600 rounded-lg"><Landmark className="h-6 w-6" /></div>
          <div>
            <h2 className="text-lg font-bold text-violet-900">Retenciones 2% — Impuesto sobre la Renta</h2>
            <p className="text-sm text-gray-500">Saldo a favor acumulado por retenciones de entidades del Estado (crédito de renta). Hacé clic en un cliente para ver el detalle de sus facturas.</p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg border p-3 flex items-center gap-3 flex-wrap">
        <label className="text-sm text-gray-600">Período:</label>
        <select value={anio} onChange={(e) => setAnio(e.target.value === 'todos' ? 'todos' : parseInt(e.target.value))} className="border rounded-lg px-3 py-2 text-sm">
          {ANIOS.map(y => <option key={y} value={y}>{y === 'todos' ? 'Todos los años' : y}</option>)}
        </select>
        <div className="flex-1" />
        <button
          onClick={handleExport}
          disabled={exporting || !data?.detalle?.length}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-green-400 text-green-600 hover:bg-green-50 disabled:opacity-40"
        >
          <FileSpreadsheet className="h-4 w-4" /> {exporting ? 'Exportando...' : 'Excel'}
        </button>
      </div>

      {/* KPIs */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 mb-1">
              <Landmark className="h-5 w-5 text-violet-600" />
              <span className="text-sm text-gray-600">Saldo a favor (crédito renta)</span>
            </div>
            <p className="text-2xl font-bold text-violet-700">{crc(data.totalRetenidoCRC)}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Deducible en la declaración de renta</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-5 w-5 text-blue-600" />
              <span className="text-sm text-gray-600">Clientes que retienen</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">{data.cantidadClientes}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 mb-1">
              <Receipt className="h-5 w-5 text-amber-600" />
              <span className="text-sm text-gray-600">Retenciones registradas</span>
            </div>
            <p className="text-2xl font-bold text-amber-700">{data.cantidadRetenciones}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-lg border p-8 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-violet-500 border-t-transparent rounded-full mx-auto" />
        </div>
      ) : !data?.detalle?.length ? (
        <div className="bg-white rounded-lg border p-10 text-center text-gray-500">
          <FileX className="h-12 w-12 mx-auto text-gray-300 mb-2" />
          <p>No hay retenciones del 2% registradas en este período.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Saldo a favor por cliente <span className="font-normal text-gray-400">— clic para ver el detalle</span></h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Cliente</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Identificación</th>
                <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Retenciones</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Saldo a favor</th>
                <th className="px-2 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.porCliente.map((c) => (
                <tr
                  key={c.clienteId}
                  onClick={() => setClienteSel(c)}
                  className="hover:bg-violet-50/60 cursor-pointer transition-colors group"
                >
                  <td className="px-4 py-2.5 font-medium text-gray-900 group-hover:text-violet-800">{c.clienteNombre}</td>
                  <td className="px-4 py-2.5 text-gray-500 font-mono text-xs">{c.identificacion || '-'}</td>
                  <td className="px-4 py-2.5 text-center text-gray-600">{c.cantidad}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-violet-700">{crc(c.totalCRC)}</td>
                  <td className="px-2 py-2.5 text-center"><ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-violet-500 inline" /></td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-violet-50 border-t-2 border-violet-200">
              <tr>
                <td colSpan={2} className="px-4 py-2 text-sm font-bold text-right">Total saldo a favor:</td>
                <td className="px-4 py-2 text-center font-bold">{data.cantidadRetenciones}</td>
                <td className="px-4 py-2 text-right text-base font-bold text-violet-700">{crc(data.totalRetenidoCRC)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Modal: detalle del cliente. Se oculta mientras se ve el preview de una FE
          (así el preview queda al frente); al cerrar el preview, vuelve. */}
      {clienteSel && !verFacturaId && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={() => setClienteSel(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-start justify-between px-6 py-4 border-b bg-gradient-to-r from-violet-50 to-white rounded-t-2xl">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-violet-100 text-violet-600 rounded-lg"><Landmark className="h-5 w-5" /></div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{clienteSel.clienteNombre}</h3>
                  <p className="text-xs text-gray-500 font-mono">{clienteSel.identificacion || 'Sin identificación'}</p>
                </div>
              </div>
              <button onClick={() => setClienteSel(null)} className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5" /></button>
            </div>

            {/* Resumen */}
            <div className="px-6 py-3 border-b bg-gray-50 flex items-center gap-6">
              <div>
                <p className="text-[11px] text-gray-500 uppercase">Saldo a favor</p>
                <p className="text-lg font-bold text-violet-700">{crc(clienteSel.totalCRC)}</p>
              </div>
              <div>
                <p className="text-[11px] text-gray-500 uppercase">Retenciones</p>
                <p className="text-lg font-bold text-gray-700">{clienteSel.cantidad}</p>
              </div>
            </div>

            {/* Detalle por factura */}
            <div className="overflow-y-auto flex-1">
              <table className="w-full text-sm">
                <thead className="bg-white sticky top-0 border-b">
                  <tr>
                    <th className="px-5 py-2 text-left text-xs font-semibold text-gray-600">Fecha cobro</th>
                    <th className="px-5 py-2 text-left text-xs font-semibold text-gray-600">Factura(s)</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">Moneda</th>
                    <th className="px-5 py-2 text-right text-xs font-semibold text-gray-600">Retención 2%</th>
                    <th className="px-5 py-2 text-right text-xs font-semibold text-gray-600">Retención (₡)</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {detalleCliente.map((d) => (
                    <tr key={d.cobroId} className="hover:bg-gray-50 align-top">
                      <td className="px-5 py-2.5 whitespace-nowrap text-gray-700">{fecha(d.fecha)}</td>
                      <td className="px-5 py-2.5">
                        <div className="flex flex-col gap-1">
                          {(d.facturasList && d.facturasList.length > 0)
                            ? d.facturasList.map(f => (
                                <div key={f.id} className="flex items-center gap-2">
                                  <span className="font-mono text-xs text-gray-700">{f.consecutivo}</span>
                                  <button
                                    onClick={() => setVerFacturaId(f.id)}
                                    className="text-violet-500 hover:text-violet-700"
                                    title="Ver factura completa"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                </div>
                              ))
                            : <span className="font-mono text-xs text-gray-400">{d.facturas}</span>}
                          {d.numeroComprobante && <span className="text-[10px] text-gray-400">Comp.: {d.numeroComprobante}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${d.moneda === 'USD' ? 'border-blue-300 text-blue-700' : 'border-gray-300 text-gray-600'}`}>{d.moneda}</span>
                      </td>
                      <td className="px-5 py-2.5 text-right text-gray-700">{money(d.retencion, d.moneda)}</td>
                      <td className="px-5 py-2.5 text-right font-semibold text-violet-700">{crc(d.retencionCRC)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-violet-50 border-t-2 border-violet-200 sticky bottom-0">
                  <tr>
                    <td colSpan={4} className="px-5 py-2.5 text-sm font-bold text-right">Total saldo a favor (₡):</td>
                    <td className="px-5 py-2.5 text-right text-base font-bold text-violet-700">{crc(clienteSel.totalCRC)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Preview de la FE completa */}
      {verFacturaId && (
        <DocumentDetailModal documentId={verFacturaId} onClose={() => setVerFacturaId(null)} />
      )}
    </div>
  );
}
